import { useState } from "react";

function VideoCard({ video, index, size = "md" }) {
  const [playing, setPlaying] = useState(false);
  const colSpan = size === "lg" ? "md:col-span-8" : size === "sm" ? "md:col-span-4" : "md:col-span-6";

  return (
    <article
      data-testid={`doc-card-${video.id}`}
      className={`group ${colSpan} col-span-1`}
    >
      <div className="yt-aspect img-zoom border border-white/5">
        {playing ? (
          <iframe
            data-testid={`doc-iframe-${video.id}`}
            src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <>
            <img
              className="yt-thumb"
              src={video.custom_thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
              alt={video.title}
              onLoad={(e) => {
                if (video.custom_thumbnail) return;
                if (e.currentTarget.naturalWidth < 320 && !e.currentTarget.dataset.fb) {
                  e.currentTarget.dataset.fb = "1";
                  e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/sddefault.jpg`;
                } else if (e.currentTarget.naturalWidth < 320 && e.currentTarget.dataset.fb === "1") {
                  e.currentTarget.dataset.fb = "2";
                  e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                }
              }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`;
              }}
            />
            <button
              data-testid={`doc-play-${video.id}`}
              className="thumb-play"
              onClick={() => setPlaying(true)}
              aria-label={`Play ${video.title}`}
            >
              <div className="play-circle">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </button>
          </>
        )}
      </div>

      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-overline text-[#C4A27A] mb-2">
            {String(index + 1).padStart(2, "0")} · {video.outlet} · {video.year}
          </div>
          <h3 className="font-serif-display text-xl md:text-2xl text-[#EAEAEA] group-hover:text-[#C4A27A] transition-colors">
            {video.title}
          </h3>
        </div>
      </div>
    </article>
  );
}

function FeaturedFilm({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <article data-testid={`doc-card-${video.id}`} className="col-span-1 md:col-span-12 mb-16 md:mb-24">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="md:col-span-8">
          <div className="yt-aspect img-zoom border border-white/10">
            {playing ? (
              <iframe
                data-testid={`doc-iframe-${video.id}`}
                src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0`}
                title={video.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <>
                <img
                  className="yt-thumb"
                  src={`https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                  alt={video.title}
                  onLoad={(e) => {
                    if (e.currentTarget.naturalWidth < 320 && !e.currentTarget.dataset.fb) {
                      e.currentTarget.dataset.fb = "1";
                      e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/sddefault.jpg`;
                    } else if (e.currentTarget.naturalWidth < 320 && e.currentTarget.dataset.fb === "1") {
                      e.currentTarget.dataset.fb = "2";
                      e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                    }
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`;
                  }}
                />
                <button
                  data-testid={`doc-play-${video.id}`}
                  className="thumb-play"
                  onClick={() => setPlaying(true)}
                  aria-label={`Play ${video.title}`}
                >
                  <div className="play-circle" style={{ width: 88, height: 88 }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="md:col-span-4 md:pt-4">
          <div className="font-mono-overline text-[#C4A27A] mb-3">
            Featured Film · {video.year}
          </div>
          <h3 className="font-serif-display text-4xl md:text-5xl leading-[1.02] text-[#EAEAEA] mb-4">
            {video.title}
          </h3>
          <div className="font-mono-overline text-[#EAEAEA]/60 mb-5">{video.outlet}</div>
          <p className="text-[#EAEAEA]/80 text-base md:text-lg leading-relaxed mb-5">
            {video.summary}
          </p>
          {video.credits && (
            <p className="text-[#EAEAEA]/55 text-sm italic mb-5">{video.credits}</p>
          )}
          {video.press_url && (
            <a
              data-testid={`doc-press-${video.id}`}
              href={video.press_url}
              target="_blank"
              rel="noopener noreferrer"
              className="edit-link text-[#EAEAEA]"
            >
              Press — MovieMaker
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Documentaries({ items = [] }) {
  const broadcast = items.filter((v) => v.category === "broadcast");
  const earlier = items.filter((v) => v.category === "documentary");
  // Asymmetric grid for broadcast items
  const sizes = ["lg", "sm", "sm", "lg", "md", "md", "md", "md"];

  return (
    <section
      id="documentaries"
      data-testid="documentaries-section"
      className="relative bg-[#121212] text-[#EAEAEA] py-16 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-[#C4A27A]" />
            <span className="font-mono-overline text-[#C4A27A]">01 — Broadcast & video</span>
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] max-w-3xl">
            From <em className="text-[#C4A27A]">the field</em>.
          </h2>
        </div>

        {broadcast.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-14 md:mb-20">
            {broadcast.map((v, i) => (
              <VideoCard key={v.id} video={v} index={i} size={sizes[i] || "md"} />
            ))}
          </div>
        )}

        {earlier.length > 0 && (
          <>
            <div className="mb-8 md:mb-10 flex items-center gap-3">
              <span className="w-10 h-px bg-[#C4A27A]" />
              <span className="font-mono-overline text-[#C4A27A]">
                Earlier independent work
              </span>
            </div>
            {earlier.map((v) => (
              <FeaturedFilm key={v.id} video={v} />
            ))}
          </>
        )}
      </div>
    </section>
  );
}
